import * as ReactDOM from "react-dom";
import { BindingError } from "../BindingError.js";
import { StateIterator } from "./state/StateIterator.js";
import { getEntityMarker } from "./state/getEntityMarker.js";
import { assertNever } from "../utils/assertNever.js";
var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class EventManager {
  constructor(asyncBatchUpdatesOptions, batchUpdatesOptions, config, dirtinessTracker, onUpdate, treeStore) {
    this.asyncBatchUpdatesOptions = asyncBatchUpdatesOptions;
    this.batchUpdatesOptions = batchUpdatesOptions;
    this.config = config;
    this.dirtinessTracker = dirtinessTracker;
    this.onUpdate = onUpdate;
    this.treeStore = treeStore;
    __publicField(this, "transactionDepth", 0);
    __publicField(this, "isFrozenWhileUpdating", false);
    __publicField(this, "isMutating", false);
    __publicField(this, "ongoingPersistOperation");
    __publicField(this, "previousMetadata");
    __publicField(this, "newlyInitializedWithListeners", /* @__PURE__ */ new Set());
    __publicField(this, "pendingWithBeforeUpdate", /* @__PURE__ */ new Set());
    __publicField(this, "rootsWithPendingUpdates", /* @__PURE__ */ new Set());
  }
  async persistOperation(operation) {
    if (this.transactionDepth > 0) {
      throw new BindingError(`Cannot trigger a persist whilst batching updates!`);
    }
    const ongoingOperation = this.ongoingPersistOperation;
    if (ongoingOperation !== void 0) {
      return await ongoingOperation;
    }
    return await (this.ongoingPersistOperation = new Promise(async (resolve, reject) => {
      this.isMutating = true;
      this.flushUpdates();
      try {
        resolve(await this.asyncOperation(operation));
      } catch (e) {
        console.error("PersistError", e);
        reject(e);
      } finally {
        this.ongoingPersistOperation = void 0;
        this.isMutating = false;
        this.flushUpdates();
      }
    }));
  }
  syncTransaction(transaction) {
    try {
      this.transactionDepth++;
      return transaction();
    } finally {
      this.transactionDepth--;
    }
  }
  syncOperation(operation) {
    const result = this.syncTransaction(operation);
    this.flushUpdates();
    return result;
  }
  async asyncTransaction(transaction) {
    try {
      this.transactionDepth++;
      return await transaction();
    } finally {
      this.transactionDepth--;
    }
  }
  async asyncOperation(operation) {
    const result = await this.asyncTransaction(operation);
    this.flushUpdates();
    return result;
  }
  flushUpdates() {
    if (this.transactionDepth > 0) {
      return;
    }
    if (this.isFrozenWhileUpdating) {
      throw new BindingError(
        `Trying to perform an update while the whole accessor tree is already updating. This is most likely caused by updating the accessor tree while rendering or from within the 'update' event handler, which is a no-op. If you wish to mutate the tree in reaction to other changes, use the 'beforeUpdate' event.`
      );
    }
    const newMetadata = this.getNewUpdateMetadata();
    if (!this.shouldFlushUpdates(newMetadata)) {
      return;
    }
    ReactDOM.unstable_batchedUpdates(() => {
      this.isFrozenWhileUpdating = true;
      this.triggerBeforeFlushEvents();
      this.onUpdate(newMetadata);
      this.flushPendingAccessorUpdates(Array.from(this.rootsWithPendingUpdates));
      this.rootsWithPendingUpdates.clear();
      this.isFrozenWhileUpdating = false;
      this.previousMetadata = newMetadata;
    });
  }
  getNewUpdateMetadata() {
    return {
      isMutating: this.isMutating
    };
  }
  shouldFlushUpdates(newMetadata) {
    if (this.transactionDepth > 0) {
      return false;
    }
    if (this.previousMetadata === void 0) {
      return true;
    }
    if (this.rootsWithPendingUpdates.size) {
      return true;
    }
    for (const key in newMetadata) {
      const fromPrevious = this.previousMetadata[key];
      const fromNew = newMetadata[key];
      if (!Object.is(fromPrevious, fromNew)) {
        return true;
      }
    }
    return false;
  }
  registerNewlyInitialized(newlyInitialized) {
    const initializeListeners = this.getEventListeners(newlyInitialized, { type: "initialize" });
    if (initializeListeners === void 0) {
      return;
    }
    this.newlyInitializedWithListeners.add(newlyInitialized);
    if (this.transactionDepth === 0) {
      this.triggerOnInitialize();
    }
  }
  registerUpdatedConnection(parentState, placeholderName) {
    if (parentState.fieldsWithPendingConnectionUpdates === void 0) {
      parentState.fieldsWithPendingConnectionUpdates = /* @__PURE__ */ new Set();
    }
    const marker = getEntityMarker(parentState);
    placeholders:
      for (const [fieldName, placeholdersByField] of marker.fields.placeholders) {
        if (typeof placeholdersByField === "string") {
          if (placeholdersByField === placeholderName) {
            parentState.fieldsWithPendingConnectionUpdates.add(fieldName);
          }
        } else {
          for (const placeholderByFieldName of placeholdersByField) {
            if (placeholderByFieldName === placeholderName) {
              parentState.fieldsWithPendingConnectionUpdates.add(fieldName);
              continue placeholders;
            }
          }
        }
      }
  }
  registerJustUpdated(justUpdated, changesDelta) {
    const beforeUpdateListeners = this.getEventListeners(justUpdated, { type: "beforeUpdate" });
    if (beforeUpdateListeners !== void 0) {
      this.pendingWithBeforeUpdate.add(justUpdated);
    }
    justUpdated.accessor = void 0;
    switch (justUpdated.type) {
      case "entityRealm":
      case "entityList": {
        justUpdated.unpersistedChangesCount += changesDelta;
        const parent = justUpdated.blueprint.parent;
        if (parent === void 0) {
          this.rootsWithPendingUpdates.add(justUpdated);
          this.dirtinessTracker.increaseBy(changesDelta);
        } else {
          if (!parent.childrenWithPendingUpdates) {
            parent.childrenWithPendingUpdates = /* @__PURE__ */ new Set();
          }
          parent.childrenWithPendingUpdates.add(justUpdated);
          this.registerJustUpdated(parent, changesDelta);
        }
        break;
      }
      case "field": {
        const parent = justUpdated.parent;
        if (!parent.childrenWithPendingUpdates) {
          parent.childrenWithPendingUpdates = /* @__PURE__ */ new Set();
        }
        parent.childrenWithPendingUpdates.add(justUpdated);
        this.registerJustUpdated(justUpdated.parent, changesDelta);
        break;
      }
    }
  }
  flushPendingAccessorUpdates(rootStates) {
    const agenda = rootStates;
    for (const state of agenda) {
      const updateListeners = state.type === "field" ? this.getEventDispatchers(state, { type: "update" }, [state.getAccessor()]) : state.type === "entityRealm" ? this.getEventDispatchers(state, { type: "update" }, [state.getAccessor()]) : this.getEventDispatchers(state, { type: "update" }, [state.getAccessor()]);
      if (updateListeners !== void 0) {
        for (const handler of updateListeners) {
          handler();
        }
      }
      if (state.type === "entityRealm" && state.fieldsWithPendingConnectionUpdates) {
        for (const updatedField of state.fieldsWithPendingConnectionUpdates) {
          const listeners = this.getEventListeners(state, { type: "connectionUpdate", key: updatedField });
          if (!listeners) {
            continue;
          }
          for (const listener of listeners) {
            listener(state.getAccessor());
          }
        }
      }
      switch (state.type) {
        case "entityRealm":
        case "entityList": {
          if (state.childrenWithPendingUpdates !== void 0) {
            for (const childState of state.childrenWithPendingUpdates) {
              agenda.push(childState);
            }
            state.childrenWithPendingUpdates.clear();
          }
          break;
        }
        case "field":
          break;
        default:
          assertNever();
      }
    }
  }
  async triggerAsyncMutatingEvent(eventType, options) {
    await this.asyncTransaction(async () => {
      const callbackQueue = [];
      for (const [, subTreeState] of StateIterator.eachRootState(this.treeStore)) {
        for (const iNode of StateIterator.depthFirstINodes(subTreeState)) {
          const listeners = this.getEventListeners(iNode, { type: eventType }) ?? [];
          for (const listener of listeners) {
            callbackQueue.push([iNode, listener]);
          }
        }
      }
      for (let waterfallDepth = 0; waterfallDepth < this.config.getValue(`${eventType}SettleLimit`); waterfallDepth++) {
        const promiseReturns = [];
        const correspondingStates = [];
        if (callbackQueue.length === 0) {
          break;
        }
        for (const [state, callback] of callbackQueue) {
          correspondingStates.push(state);
          const changesCountBefore2 = this.dirtinessTracker.getTotalTouchCount();
          let validateChanges = false;
          try {
            const result = callback(state.getAccessor, options);
            if (result instanceof Promise) {
              validateChanges = true;
              promiseReturns.push(result);
            } else {
              promiseReturns.push(Promise.resolve(result));
            }
          } catch (e) {
            promiseReturns.push(Promise.reject(e));
          }
          if (validateChanges) {
            this.validateWithoutChanges(changesCountBefore2, eventType);
          }
        }
        const changesCountBefore = this.dirtinessTracker.getTotalTouchCount();
        const newCallbacks = await Promise.allSettled(promiseReturns);
        this.validateWithoutChanges(changesCountBefore, eventType);
        callbackQueue.length = 0;
        for (const newlyInitialized of this.newlyInitializedWithListeners) {
          if (newlyInitialized.type === "field") {
            continue;
          }
          const beforePersistHandlers = this.getEventListeners(newlyInitialized, { type: eventType });
          if (beforePersistHandlers) {
            for (const listener of beforePersistHandlers) {
              callbackQueue.push([newlyInitialized, listener]);
            }
          }
        }
        this.triggerBeforeFlushEvents();
        this.handleRejections(eventType, newCallbacks);
        for (let i = 0; i < newCallbacks.length; i++) {
          const result = newCallbacks[i];
          const correspondingState = correspondingStates[i];
          if (result.status === "fulfilled") {
            if (typeof result.value === "function") {
              callbackQueue.push([correspondingState, result.value]);
            }
          }
        }
      }
    });
  }
  triggerBeforeFlushEvents() {
    this.syncTransaction(() => {
      const settleLimit = this.config.getValue("beforeUpdateSettleLimit");
      for (let attemptNumber = 0; attemptNumber < settleLimit; attemptNumber++) {
        if (!this.pendingWithBeforeUpdate.size) {
          this.triggerOnInitialize();
          return;
        }
        const withBeforeUpdate = this.pendingWithBeforeUpdate;
        this.pendingWithBeforeUpdate = /* @__PURE__ */ new Set();
        for (const state of withBeforeUpdate) {
          const listeners = this.getEventListeners(state, { type: "beforeUpdate" });
          if (listeners === void 0) {
            continue;
          }
          switch (state.type) {
            case "field":
              for (const listener of listeners) {
                listener(state.getAccessor());
              }
              break;
            case "entityRealm":
            case "entityList":
              for (const listener of listeners) {
                state.getAccessor().batchUpdates(listener);
              }
              break;
            default:
              return assertNever();
          }
        }
        this.triggerOnInitialize();
      }
      throw new BindingError(
        `Maximum stabilization limit of updates caused by 'beforeUpdate' exceeded. This likely means there is an infinite feedback loop in your code.`
      );
    });
  }
  triggerOnInitialize() {
    this.syncTransaction(() => {
      for (const state of this.newlyInitializedWithListeners) {
        const listeners = state.type === "field" ? this.getEventDispatchers(state, { type: "initialize" }, [state.getAccessor, this.batchUpdatesOptions]) : state.type === "entityRealm" ? this.getEventDispatchers(state, { type: "initialize" }, [state.getAccessor, this.batchUpdatesOptions]) : this.getEventDispatchers(state, { type: "initialize" }, [state.getAccessor, this.batchUpdatesOptions]);
        if (listeners) {
          for (const listener of listeners) {
            listener();
          }
        }
        if (state.type === "entityRealm") {
          state.entity.hasIdSetInStone = true;
        }
      }
      this.newlyInitializedWithListeners.clear();
    });
  }
  async triggerOnPersistError(options) {
    await this.asyncTransaction(async () => {
      const handlerPromises = [];
      for (const [, subTreeState] of StateIterator.eachRootState(this.treeStore)) {
        for (const iNode of StateIterator.depthFirstINodes(subTreeState)) {
          const dispatchers = iNode.type === "entityRealm" ? this.getEventDispatchers(iNode, { type: "persistError" }, [iNode.getAccessor, options]) : this.getEventDispatchers(iNode, { type: "persistError" }, [iNode.getAccessor, options]);
          if (dispatchers === void 0) {
            continue;
          }
          for (const listener of dispatchers ?? []) {
            try {
              const result = listener();
              if (result instanceof Promise) {
                handlerPromises.push(result);
              } else {
                handlerPromises.push(Promise.resolve(result));
              }
            } catch (e) {
              handlerPromises.push(Promise.reject(e));
            }
          }
        }
      }
      const handlerResults = await Promise.allSettled(handlerPromises);
      this.handleRejections("persistError", handlerResults);
    });
  }
  async triggerOnBeforePersist() {
    return this.triggerAsyncMutatingEvent("beforePersist", this.asyncBatchUpdatesOptions);
  }
  async triggerOnPersistSuccess(options) {
    return this.triggerAsyncMutatingEvent("persistSuccess", options);
  }
  getEventListeners(state, event) {
    if (!state.eventListeners) {
      return void 0;
    }
    return state.eventListeners.get(event);
  }
  getEventDispatchers(state, event, listenerArgs) {
    const eventListeners = this.getEventListeners(state, event);
    if (eventListeners === void 0) {
      return void 0;
    }
    return Array.from(eventListeners.values(), (listener) => {
      return () => listener(...listenerArgs);
    });
  }
  handleRejections(handler, promises) {
    const rejections = promises.filter((it) => it.status === "rejected");
    if (rejections.length === 0) {
      return;
    }
    rejections.forEach((it) => console.error(it.reason));
  }
  validateWithoutChanges(initialCount, eventType) {
    this.dirtinessTracker.getTotalTouchCount();
  }
}
__publicField(EventManager, "NO_CHANGES_DIFFERENCE", 0);
export {
  EventManager
};
//# sourceMappingURL=EventManager.js.map
